/**
 * IssueDetailsTab - Shows issue details and allows status/assignment changes.
 */

import { useState } from 'react'
import { format } from 'date-fns'
import {
  User,
  Calendar,
  Tag,
  AlertCircle,
  CheckCircle,
  Clock,
  MessageSquare,
  XCircle,
  Loader2,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Separator } from '@/components/ui/separator'
import { useToast } from '@/hooks/use-toast'
import { useChangeIssueStatus, useUpdateIssue, useValueComparison } from '@/hooks/useAnnotations'
import type { Annotation, IssueStatus, IssuePriority } from '@/types/annotation'
import {
  ISSUE_STATUS_LABELS,
  ISSUE_PRIORITY_LABELS,
  ISSUE_PRIORITY_COLORS,
} from '@/types/annotation'

interface IssueDetailsTabProps {
  issue: Annotation
  studyId: string
}

export function IssueDetailsTab({ issue, studyId }: IssueDetailsTabProps) {
  const { toast } = useToast()

  // Status change modal state
  const [showStatusModal, setShowStatusModal] = useState(false)
  const [newStatus, setNewStatus] = useState<IssueStatus | ''>('')
  const [changeReason, setChangeReason] = useState('')

  // Mutations
  const changeStatus = useChangeIssueStatus(studyId)
  const updateIssue = useUpdateIssue(studyId)

  // Value comparison (if issue has valueAtCreation)
  const { data: valueComparison } = useValueComparison(
    issue.valueAtCreation ? studyId : undefined,
    issue.valueAtCreation ? issue.id : null
  )

  // Available roles (would come from API in real app)
  const availableRoles = ['Data Manager', 'Clinical Reviewer', 'Statistical Analyst', 'Medical Monitor']

  const handleStatusChange = async () => {
    if (!newStatus || !changeReason.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Please provide a reason for the status change',
        variant: 'destructive',
      })
      return
    }

    try {
      await changeStatus.mutateAsync({
        annotationId: issue.id,
        data: { newStatus, changeReason },
      })
      setShowStatusModal(false)
      setNewStatus('')
      setChangeReason('')
    } catch {
      // Error handled by hook
    }
  }

  const handlePriorityChange = async (priority: IssuePriority) => {
    try {
      await updateIssue.mutateAsync({
        annotationId: issue.id,
        data: { priority },
      })
    } catch {
      // Error handled by hook
    }
  }

  const handleAssignmentChange = async (role: string) => {
    try {
      // Explicitly send null when clearing assignment, role value otherwise
      await updateIssue.mutateAsync({
        annotationId: issue.id,
        data: { assignedToRole: role === '' || role === 'unassigned' ? null : role },
      })
    } catch {
      // Error handled by hook
    }
  }

  const getStatusIcon = (status: IssueStatus) => {
    switch (status) {
      case 'open':
        return <AlertCircle className="h-4 w-4 text-blue-500" />
      case 'in_review':
        return <Clock className="h-4 w-4 text-yellow-500" />
      case 'query_raised':
        return <MessageSquare className="h-4 w-4 text-orange-500" />
      case 'resolved':
        return <CheckCircle className="h-4 w-4 text-green-500" />
      case 'closed':
        return <XCircle className="h-4 w-4 text-gray-500" />
      default:
        return null
    }
  }

  return (
    <div className="space-y-6">
      {/* Status Section */}
      <div className="space-y-3">
        <Label className="text-sm font-medium">Status</Label>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 p-2 bg-muted rounded-lg flex-1">
            {issue.status && getStatusIcon(issue.status)}
            <span className="font-medium">
              {issue.status && ISSUE_STATUS_LABELS[issue.status]}
            </span>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowStatusModal(true)}
            disabled={issue.status === 'closed'}
          >
            Change Status
          </Button>
        </div>
      </div>

      {/* Priority Section */}
      <div className="space-y-3">
        <Label className="text-sm font-medium">Priority</Label>
        <Select
          value={issue.priority || ''}
          onValueChange={(v) => handlePriorityChange(v as IssuePriority)}
          disabled={updateIssue.isPending}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select priority" />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(ISSUE_PRIORITY_LABELS).map(([key, label]) => (
              <SelectItem key={key} value={key}>
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${ISSUE_PRIORITY_COLORS[key as IssuePriority]?.replace('bg-', 'bg-')}`} />
                  {label}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Assignment Section */}
      <div className="space-y-3">
        <Label className="text-sm font-medium">Assigned To</Label>
        <Select
          value={issue.assignedToRole || 'unassigned'}
          onValueChange={(v) => handleAssignmentChange(v === 'unassigned' ? '' : v)}
          disabled={updateIssue.isPending}
        >
          <SelectTrigger>
            <SelectValue placeholder="Unassigned" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="unassigned">Unassigned</SelectItem>
            {availableRoles.map((role) => (
              <SelectItem key={role} value={role}>
                {role}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Separator />

      {/* Value Comparison (if available) */}
      {issue.valueAtCreation && (
        <div className="space-y-3">
          <Label className="text-sm font-medium">Value Comparison</Label>
          <div className="p-3 bg-muted rounded-lg space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">At Creation:</span>
              <span className="font-mono">{issue.valueAtCreation}</span>
            </div>
            {valueComparison && (
              <>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Current:</span>
                  <span className="font-mono">{valueComparison.currentValue ?? 'N/A'}</span>
                </div>
                {valueComparison.valueChanged && (
                  <div className="text-xs text-yellow-600 bg-yellow-50 dark:bg-yellow-900/20 p-2 rounded">
                    Value has changed since issue creation
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Metadata */}
      <div className="space-y-3">
        <Label className="text-sm font-medium">Details</Label>
        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-2 text-muted-foreground">
            <User className="h-4 w-4" />
            <span>Created by {issue.creator?.fullName || 'Unknown'}</span>
          </div>
          <div className="flex items-center gap-2 text-muted-foreground">
            <Calendar className="h-4 w-4" />
            <span>{format(new Date(issue.createdAt), 'PPpp')}</span>
          </div>
          {issue.updatedAt !== issue.createdAt && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Clock className="h-4 w-4" />
              <span>Updated {format(new Date(issue.updatedAt), 'PPpp')}</span>
            </div>
          )}
          {issue.category && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Tag className="h-4 w-4" />
              <span>Category: {issue.category.replace(/_/g, ' ')}</span>
            </div>
          )}
        </div>
      </div>

      {/* Record Key (if available) */}
      {issue.recordKey && Object.keys(issue.recordKey).length > 0 && (
        <div className="space-y-3">
          <Label className="text-sm font-medium">Record Key</Label>
          <div className="p-3 bg-muted rounded-lg">
            <pre className="text-xs font-mono overflow-x-auto">
              {JSON.stringify(issue.recordKey, null, 2)}
            </pre>
          </div>
        </div>
      )}

      {/* Status Change Modal */}
      <Dialog open={showStatusModal} onOpenChange={setShowStatusModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Issue Status</DialogTitle>
            <DialogDescription>
              Select a new status and provide a reason for the change.
              This will be recorded in the audit history.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>New Status</Label>
              <Select value={newStatus} onValueChange={(v) => setNewStatus(v as IssueStatus)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(ISSUE_STATUS_LABELS)
                    .filter(([key]) => key !== issue.status)
                    .map(([key, label]) => (
                      <SelectItem key={key} value={key}>
                        <div className="flex items-center gap-2">
                          {getStatusIcon(key as IssueStatus)}
                          {label}
                        </div>
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Reason for Change *</Label>
              <Textarea
                placeholder="Explain why you're changing the status..."
                value={changeReason}
                onChange={(e) => setChangeReason(e.target.value)}
                className="min-h-[100px]"
              />
              <p className="text-xs text-muted-foreground">
                Required for 21 CFR Part 11 compliance
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowStatusModal(false)}
              disabled={changeStatus.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleStatusChange}
              disabled={!newStatus || !changeReason.trim() || changeStatus.isPending}
            >
              {changeStatus.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Confirm Change
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
